import Immutable from 'immutable';
import { cloneDeep } from 'lodash';
import type { Slide, TimeAwareMediaAsset } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type { BoundingBox, SceneAttributesResponse } from 'js/src/client';
import type { TimingMetadata } from 'js/src/components/app/OverlaySelector';
import { Toast } from 'js/src/components/common/Toast';
import { clearTrackedSlideTextAssets, getSwapSceneDesignSlideId } from 'js/src/data/scenes';
import type { DisplayGroupId, SceneDesignDisplayName, SceneDesignId } from 'js/src/data/templates';
import {
    canUserSeeSceneDesign,
    getSceneDesignDisplayGroup,
    getSceneDesignDisplayName,
    getTemplateBundle,
    getTemplateDefaultVoiceoverSceneDesign,
    getTemplateDisplayGroups,
    getTemplateSceneIds,
} from 'js/src/data/templates';
import {
    setBrightnessUserOverride,
    setCropUserOverride,
    setKenburnUserOverride,
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import type { ImmutableMap } from 'js/src/libs/consts';
import {
    DYNAMIC_DISPLAY_GROUPS,
    ImmutableFromJS,
    ImmutableToJS,
    SceneDesignSpecialCase,
} from 'js/src/libs/consts';
import { cropToBoundingBox } from 'js/src/libs/crop-utils';
import SceneSwapper from 'js/src/libs/scene-swapping/SceneSwapper';
import uuid from 'js/src/libs/uuid';
import { CreateOverlayError } from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    applyTalkingHeadMediaOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
    makeVoicedrivenCompatibleScene,
    replaceSceneGroupOperation,
    replaceSlideOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getVoiceDrivenAsset,
    isTalkingHeadSceneGroup,
    sceneContainsEditableMedia,
    sceneContainsIcon,
} from 'js/src/luminary/luminary-selectors';
import { isVoiceDrivenCompatibleSceneCategory } from 'js/src/luminary/luminary-utils';

export type TimingDataType = {
    OPEN_0: number | null;
    SCENE_ATTRIBUTES_REQUESTED_1: number | null;
    SCENE_ATTRIBUTES_RECEIVED_2: number | null;
    TEXT_SIZING_REQUESTED_3: number | null;
    LUMINARY_SCENES_ADDED_4: number | null;
    LUMINARY_AUTO_DRAW_ENABLED_5: number | null;
    TEXT_SIZING_RECEIVED_6: number | null;
    LUMINARY_STARTING_RENDER_7: number | null;
    LUMINARY_FINISHED_RENDERS_8: number | null;
};

export type SwapSceneDesignRenderedData = {
    sceneDesign: string;
    sceneCategory: string;
    displayGroup: number;
    displayName?: SceneDesignDisplayName;
    slide_id: string;
    specialCase: SceneDesignSpecialCase;
};

export const guessSpecialCase = (scene: Slide) => {
    const linkedMediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).filter((asset) => {
        return asset.isEditable && asset.linkToSceneGroup === true;
    });
    if (linkedMediaAssets.length > 1) {
        return SceneDesignSpecialCase.MultipleSpeakers;
    }
    return SceneDesignSpecialCase.NONE;
};
/**
 * For performance reasons we don't want to copy too many subscenes to preview.
 * We copy just few of them, and when user selects particular scene design we create new scene with all subscenes
 */
const MAX_SUBSCENES_IN_PREVIEW = 2;

export const applyRecommendedSceneOverridesOperation = createLuminaryOperation(
    (
        { luminary },
        sceneId: string,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
    ) => {
        const targetScene = luminary.getSceneById(sceneId);
        if (!targetScene) {
            return;
        }

        if (!sceneAttributes) {
            return;
        }

        const sceneAttribute = sceneAttributes[targetScene.sceneDesign];
        if (!sceneAttribute) {
            return;
        }

        const textPositionPreset = targetScene.availableTextPositions.includes(
            sceneAttribute.text_pos,
        )
            ? sceneAttribute.text_pos
            : targetScene.availableTextPositions[0];
        targetScene.applyTextPreset(textPositionPreset);

        sceneAttribute.media_attributes.forEach((attrs) => {
            const asset = targetScene.getAssetByName(attrs.asset_name) as TimeAwareMediaAsset;
            if (!asset) {
                return;
            }

            // Check if the fillmode is something other than cover. If so we shouldn't set any of the
            // media attributes for it since they aren't applicable for non-cover'ed assets.
            // TODO: ideally this would be better to do further upstream to avoid duplicate logic here
            if (asset.fillMode !== VideoData.FILL_COVER) {
                return;
            }
            if (asset.linkToSceneGroup === true) {
                return;
            }

            asset.brightness = attrs.brightness;
            if (attrs.x_offset !== null && attrs.y_offset !== null) {
                // null if no crop suggested
                asset.xTranslation = attrs.x_offset;
                asset.yTranslation = attrs.y_offset;
            }
            asset.kenburn = attrs.background_animation;
        });
    },
);

export const getMultipleSpeakersSceneDesignIdsOperation = createLuminaryOperation(
    (
        { getState, luminary },
        sceneDesignIds: Immutable.List<SceneDesignId>,
        faceDetections: BoundingBox[],
    ): Immutable.List<SceneDesignId> => {
        if (faceDetections.length === 0) {
            return Immutable.List<SceneDesignId>();
        }

        const MAX_OPTIONS_TO_RECOMMEND = 3;

        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });
        try {
            dataLuminary.setTemplate(
                ImmutableToJS(getTemplateBundle(getState(), luminary.template.id)),
            );

            const INELIGIBLE_SCORE = -1;
            const calculateScoreFactors = (scene: Slide) => {
                const mediaAssets = scene
                    .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                    .filter((asset) => {
                        return asset.isEditable;
                    });

                if (mediaAssets.length <= 1) {
                    // split-and-stack only makes sense if there are multiple media assets in the scene design
                    return {
                        variance: INELIGIBLE_SCORE,
                        facesVsMediaAssetsRatio: INELIGIBLE_SCORE,
                    };
                }

                // favour scene designs where the media assets are of equal sizes
                const mediaAssetSizes = mediaAssets.map(
                    (asset) =>
                        asset.mediaTargetResolution.width * asset.mediaTargetResolution.height,
                );
                const meanSize =
                    mediaAssetSizes.reduce((acc, size) => acc + size, 0) / mediaAssetSizes.length;
                const variance =
                    mediaAssetSizes.reduce((acc, size) => acc + (size - meanSize) ** 2, 0) /
                    mediaAssetSizes.length; // we want to minimize the variance

                // prioritize recommending scene designs where the number of media assets = number of faces
                const facesVsMediaAssetsRatio = Math.abs(
                    1 - faceDetections.length / mediaAssets.length,
                ); // we want this to be as small as possible

                return {
                    variance,
                    facesVsMediaAssetsRatio,
                };
            };

            const scenesAndScoreFactors = sceneDesignIds
                .map((sceneDesignId) => {
                    const { scene } = dataLuminary.addSceneGroup({ sceneDesignId });
                    return {
                        scene,
                        scoreFactors: calculateScoreFactors(scene),
                    };
                })
                .filter(({ scoreFactors }) => scoreFactors.variance !== INELIGIBLE_SCORE);

            // normalize the score factors to be between 0 and 1
            const maxVariance = Math.max(
                ...scenesAndScoreFactors.map(({ scoreFactors }) => scoreFactors.variance),
            );
            const maxFacesVsMediaAssetsRatio = Math.max(
                ...scenesAndScoreFactors.map(
                    ({ scoreFactors }) => scoreFactors.facesVsMediaAssetsRatio,
                ),
            );
            scenesAndScoreFactors.forEach(({ scoreFactors }) => {
                scoreFactors.variance = scoreFactors.variance / maxVariance;
                scoreFactors.facesVsMediaAssetsRatio =
                    scoreFactors.facesVsMediaAssetsRatio / maxFacesVsMediaAssetsRatio;
            });

            // calculate the score
            const scenesAndScores = scenesAndScoreFactors.map(({ scene, scoreFactors }) => {
                return {
                    scene,
                    score: scoreFactors.variance + scoreFactors.facesVsMediaAssetsRatio,
                };
            });

            const sortedScenesAndScores = scenesAndScores.sort((a, b) => a.score - b.score);
            return sortedScenesAndScores
                .map(({ scene }) => scene.sceneDesign)
                .slice(0, MAX_OPTIONS_TO_RECOMMEND);
        } finally {
            dataLuminary.destroy();
        }
    },
);

export const setUpMultipleSpeakersSceneOperation = createLuminaryOperation(
    ({ call }, sceneId: string, faceDetections: BoundingBox[]) => {
        // multiple-speakers-scenes are a special case: we want to set up multiple media assets
        // cropped to each speaker's face, and also clear all the text assets
        call(setCropToMatchFaceDetectionsOperation, sceneId, faceDetections);
        call(clearAllTextOperation, sceneId);
    },
);

const setCropToMatchFaceDetectionsOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, faceDetections: BoundingBox[]) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }
        const sceneGroup = scene.sceneGroup;
        const voicedrivenAsset = sceneGroup.mediaAsset;
        if (!voicedrivenAsset) {
            return;
        }

        const editableMediaAssets = scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .filter((asset) => {
                return asset.isEditable;
            })
            .sort((a, b) => {
                if (a.linkToSceneGroup) {
                    return -Infinity;
                }
                if (b.linkToSceneGroup) {
                    return Infinity;
                }
                return (
                    a.mediaTargetResolution.width * a.mediaTargetResolution.height -
                    b.mediaTargetResolution.width * b.mediaTargetResolution.height
                );
            });

        faceDetections.forEach((faceDetection, i) => {
            const asset = editableMediaAssets[i];
            if (!asset) {
                return;
            }
            asset.linkToSceneGroup = true;

            cropToBoundingBox(faceDetection, asset, voicedrivenAsset.sourceData);
        });
    },
);

export const clearAllTextOperation = createLuminaryOperation(({ luminary }, sceneId: string) => {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }

    scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((asset) => {
        asset.text = '';
    });
});

export const setupLocalLuminaryOperation = createLuminaryOperation(
    async (
        { luminary, getState, call },
        sceneId: string,
        localLuminary: Luminary,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        _setTimingData: (key: keyof TimingDataType) => void,
    ) => {
        // we want to get other scene designs in the same category to show as possible options
        // more info: https://www.notion.so/lumen5/A-Guide-To-Scene-Categories-and-Slide-Types-and-Scene-Design-Types-188e93ced4934bc8b3eadf6bb159923e
        const originalScene = luminary.getSceneById(sceneId);
        if (!originalScene) {
            throw new Error('Invalid sceneId');
        }

        // extract content to copy over from source scene
        const sceneSwapper = new SceneSwapper(originalScene, sourceSentenceToUseInstead);

        // clear the this.localLuminary of any other video that might be inside of it
        await localLuminary.clear();

        const renderedData = localLuminary.batchRun(() => {
            // set video level properties
            localLuminary.capabilities.noBranding = true;
            localLuminary.capabilities.customWatermark = true;
            localLuminary.aspectRatio = luminary.aspectRatio;
            localLuminary.setTemplate(
                ImmutableToJS(getTemplateBundle(getState(), luminary.template.id)),
            );
            localLuminary.template.transitionsEnabled = false;
            localLuminary.template.colors = luminary.template.colors;
            localLuminary.template.fonts = luminary.template.fonts;
            const templateWatermarkSources = luminary.template.watermarkSources;
            const templateWatermarkSettings = luminary.template.watermark.save();
            localLuminary.template.replaceWatermarkSources(cloneDeep(templateWatermarkSources));
            localLuminary.template.watermark.load(templateWatermarkSettings);
            const grading = luminary.template.grading;
            if (grading && localLuminary.template.grading !== grading) {
                localLuminary.template.addGrading();
                localLuminary.template.grading!.source = grading.source;
                localLuminary.template.grading!.intensity = grading.intensity;
            }
            const voiceoverBaseSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
                getState(),
                luminary.template.id,
            );
            const sceneDesignIds = getTemplateSceneIds(getState(), luminary.template.id).filter(
                (sceneDesignId) => {
                    // the user must be able to access the scene design. And also, right now we don't ever allow the base
                    // voiceover scene design to be swapped into. Currently, there is no valid use-case for that.
                    return (
                        canUserSeeSceneDesign(getState(), sceneDesignId) &&
                        sceneDesignId !== voiceoverBaseSceneDesignId
                    );
                },
            );

            const displayGroups = getTemplateDisplayGroups(getState(), luminary.template.id);
            const getSceneDesignsInDisplayGroup = (displayGroupId: DisplayGroupId) => {
                return sceneDesignIds.filter((sceneDesignId) => {
                    return getSceneDesignDisplayGroup(getState(), sceneDesignId) === displayGroupId;
                });
            };

            /**
             * We want to render scene designs in "right" order.
             * First off all we render designs in the same display group as initial slide.
             * Then other designs in groups base on display group order.
             */
            type SceneDesignToRender = {
                sceneDesignId: SceneDesignId;
                specialCase: SceneDesignSpecialCase;
            };
            const orderedSceneDesigns: SceneDesignToRender[] = [];
            // initial displayGroup of slide
            const initialDisplayGroup = getSceneDesignDisplayGroup(
                getState(),
                originalScene.sceneDesign,
            );

            if (isTalkingHeadSceneGroup(originalScene.sceneGroup) && faceDetections.length > 1) {
                const multipleSpeakersSceneDesignIds = call(
                    getMultipleSpeakersSceneDesignIdsOperation,
                    sceneDesignIds,
                    faceDetections,
                );
                if (multipleSpeakersSceneDesignIds.size > 0) {
                    orderedSceneDesigns.push(
                        ...multipleSpeakersSceneDesignIds.map((sceneDesignId) => ({
                            sceneDesignId,
                            specialCase: SceneDesignSpecialCase.MultipleSpeakers,
                        })),
                    );
                }
            }

            if (initialDisplayGroup !== undefined) {
                // we place all "siblings designs" first
                orderedSceneDesigns.push(
                    ...getSceneDesignsInDisplayGroup(initialDisplayGroup).map((sceneDesignId) => ({
                        sceneDesignId,
                        specialCase: SceneDesignSpecialCase.NONE,
                    })),
                );
            }

            // then other designs by sortedDisplayGroupList
            displayGroups.forEach((displayGroup) => {
                const sceneDesignsInDisplayGroup = getSceneDesignsInDisplayGroup(
                    displayGroup.get('id'),
                );
                if (
                    sceneDesignsInDisplayGroup.size > 0 &&
                    initialDisplayGroup !== displayGroup.get('id')
                ) {
                    orderedSceneDesigns.push(
                        ...sceneDesignsInDisplayGroup.map((sceneDesignId) => ({
                            sceneDesignId,
                            specialCase: SceneDesignSpecialCase.NONE,
                        })),
                    );
                }
            });

            // sceneDesignIds - should be sorted in order of display groups
            // create a slide for each scene design, applying the editable and recommended properties
            let renderedData: Immutable.OrderedMap<
                string,
                ImmutableMap<SwapSceneDesignRenderedData>
            > = Immutable.OrderedMap();
            orderedSceneDesigns.forEach((sceneDesignToRender) => {
                const sceneDesignInfo = localLuminary.getSceneDesignInfo(
                    sceneDesignToRender.sceneDesignId,
                );
                if (!sceneSwapper.canSwapToCategory(sceneDesignInfo.sceneCategory)) {
                    // we can't swap to this scene design, so don't show it in the selector
                    return;
                }

                const previewScene = localLuminary.addSceneGroup({
                    sceneDesignId: sceneDesignToRender.sceneDesignId,
                }).scene;

                // copy content (text, media, icons, user-editable settings) over to the target scene
                sceneSwapper.copyContentToScene(previewScene, {
                    maxSubscenesToCopy: MAX_SUBSCENES_IN_PREVIEW,
                });

                call(applyRecommendedSceneOverridesOperation, previewScene.id, sceneAttributes, {
                    luminary: localLuminary,
                });

                if (sceneDesignToRender.specialCase === SceneDesignSpecialCase.MultipleSpeakers) {
                    call(setUpMultipleSpeakersSceneOperation, previewScene.id, faceDetections, {
                        luminary: localLuminary,
                    });
                }

                // store the rendered data for this scene option
                const displayGroup =
                    sceneDesignToRender.specialCase === SceneDesignSpecialCase.MultipleSpeakers
                        ? DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.MultipleSpeakers]
                        : getSceneDesignDisplayGroup(getState(), previewScene.sceneDesign);
                if (displayGroup === undefined) {
                    throw new Error('displayGroup is undefined');
                }

                const newEntry = ImmutableFromJS<SwapSceneDesignRenderedData>({
                    sceneDesign: previewScene.sceneDesign,
                    sceneCategory: previewScene.sceneCategory,
                    displayGroup,
                    displayName: getSceneDesignDisplayName(getState(), previewScene.sceneDesign),
                    slide_id: previewScene.id,
                    specialCase: sceneDesignToRender.specialCase,
                });
                renderedData = renderedData.set(previewScene.id, newEntry);
            });

            _setTimingData('LUMINARY_SCENES_ADDED_4');
            return renderedData;
        });
        _setTimingData('LUMINARY_AUTO_DRAW_ENABLED_5');
        return renderedData;
    },
);

export const swapSceneDesignOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        localLuminary: Luminary,
        renderedData: ImmutableMap<SwapSceneDesignRenderedData>,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
    ) => {
        const previewScene = localLuminary.getSceneById(renderedData.get('slide_id'));

        const swapSceneDesignSlideId = getSwapSceneDesignSlideId(getState());
        if (!swapSceneDesignSlideId) {
            // the scene was deleted or somehow changed? close the selector
            Toast.error('The scene was deleted or changed. Please try again.');
            return;
        }

        const originalScene = luminary.getSceneById(swapSceneDesignSlideId);
        if (!previewScene || !originalScene) {
            // oops, invalid scene!
            return;
        }
        const initialSceneCategory = originalScene.sceneCategory;
        const initialSceneDesignId = originalScene.sceneDesign;
        const swapSceneDesignSceneGroupId = originalScene.sceneGroup.id;
        const targetSceneCategory = previewScene.sceneCategory;

        const { scene: newScene } = localLuminary.addSceneGroup({
            sceneDesignId: previewScene.sceneDesign,
        });

        const sceneSwapper = new SceneSwapper(originalScene, sourceSentenceToUseInstead);
        sceneSwapper.copyContentToScene(newScene, {
            maxSubscenesToCopy: MAX_SUBSCENES_IN_PREVIEW,
        });
        call(applyRecommendedSceneOverridesOperation, newScene.id, sceneAttributes, {
            luminary: localLuminary,
        });

        if (renderedData.get('specialCase') === SceneDesignSpecialCase.MultipleSpeakers) {
            call(setUpMultipleSpeakersSceneOperation, newScene.id, faceDetections, {
                luminary: localLuminary,
            });
        }

        const parentActionGroup = uuid();

        // replaceSceneGroup serializes the newScene's SceneGroup and .load()'s that into the swapSceneDesignSceneGroup
        if (sceneSwapper.swapWillAlterSceneGroup(targetSceneCategory)) {
            // some swaps will alter things at the SceneGroup level, so we need to replace the entire SceneGroup.
            call(
                replaceSceneGroupOperation,
                swapSceneDesignSceneGroupId,
                newScene.sceneGroup,
                false,
                parentActionGroup,
            );
        } else {
            // most swaps will only alter the Scene, so we can just replace the Scene. In this case we actually
            // don't want to replace the whole SceneGroup, as we don't want to affect other Scenes that might be in the SceneGroup.
            call(replaceSlideOperation, swapSceneDesignSlideId, newScene, false, parentActionGroup);
        }

        if (
            targetSceneCategory === VideoData.CATEGORY_TALKING_HEAD &&
            initialSceneCategory !== VideoData.CATEGORY_TALKING_HEAD
        ) {
            void call(applyTalkingHeadMediaOperation, {
                sceneGroupId: swapSceneDesignSceneGroupId,
                media: undefined, // use existing media
                language_code: undefined, // unknown language code
                forceCaptionGeneration: false,
                enhanceAudio: false,
                recommendOverlays: true,
                systemTriggered: false,
                parentActionGroup,
            });
        }

        // Clear any user overrides for the scene that are changed
        // when switching scene designs. That's ALL of the user overrides
        // EXCEPT for duration.
        const isAutomatic = true;
        const systemTriggered = false;
        dispatch(
            setTextPositionUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextSizeUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextAlignmentUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setCropUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setKenburnUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setBrightnessUserOverride({
                sceneId: swapSceneDesignSlideId,
                isAutomatic,
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Scene design swapped', {
            sceneId: swapSceneDesignSlideId,
            sceneGroupId: swapSceneDesignSceneGroupId,
            sceneDesignId: initialSceneDesignId,
            newSceneDesignId: newScene.sceneDesign,
            newSceneCategory: newScene.sceneCategory,
            templateId: localLuminary.template.id,
            hasIcon: sceneContainsIcon(newScene),
        });
        dispatch(
            clearTrackedSlideTextAssets({
                sceneId: swapSceneDesignSlideId,
            }),
        );

        void call(fetchAndSetRecommendedSceneAttributesOperation, {
            sceneId: swapSceneDesignSlideId,
            systemTriggered: true,
            parentActionGroup,
        });
    },
);

export const createMultiSpeakerSceneOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        sceneGroupId: string,
        originalSceneId: string,
        faceDetections: BoundingBox[],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const templateId = luminary.template.id;
        const voiceoverBaseSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(
            getState(),
            templateId,
        );
        const sceneDesignIds = getTemplateSceneIds(getState(), templateId).filter(
            (sceneDesignId) => {
                const sceneDesignInfo = luminary.getSceneDesignInfo(sceneDesignId);
                const sceneCategory = sceneDesignInfo.sceneCategory;
                return (
                    canUserSeeSceneDesign(getState(), sceneDesignId) &&
                    sceneDesignId !== voiceoverBaseSceneDesignId &&
                    isVoiceDrivenCompatibleSceneCategory(sceneCategory)
                );
            },
        );
        const multipleSpeakersSceneDesignIds = call(
            getMultipleSpeakersSceneDesignIdsOperation,
            sceneDesignIds,
            faceDetections,
        );
        if (multipleSpeakersSceneDesignIds.size === 0) {
            throw new CreateOverlayError('No scene designs found for multiple speakers');
        }

        const orginalScene = luminary.getSceneById(originalSceneId);
        if (!orginalScene) {
            throw new CreateOverlayError('Original Scene missing');
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new CreateOverlayError('Scene Group missing');
        }
        if (!isTalkingHeadSceneGroup(sceneGroup)) {
            throw new CreateOverlayError('Scene Group is not a Talking Head scene group');
        }
        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) {
            throw new CreateOverlayError('Voice Driven asset missing');
        }
        const sceneIndex = orginalScene.localIndex;

        luminary.batchRun(() => {
            const newOverlayScene = sceneGroup.addScene({
                sceneDesignId: multipleSpeakersSceneDesignIds.first(),
            });
            newOverlayScene.duration = orginalScene.duration;
            makeVoicedrivenCompatibleScene(newOverlayScene);
            sceneGroup.moveSceneToIndex(newOverlayScene.id, sceneIndex + 1);
            call(setUpMultipleSpeakersSceneOperation, newOverlayScene.id, faceDetections);
            sceneGroup.removeScene(orginalScene.id);

            const analyticsPayload: TimingMetadata = {
                method: 'crop-modal',
                hasMedia: sceneContainsEditableMedia(newOverlayScene),
                sceneId: newOverlayScene.id,
                sceneGroupId: sceneGroup.id,
                sceneDesignId: multipleSpeakersSceneDesignIds.first(),
                duration: newOverlayScene.endTime - newOverlayScene.startTime,
                templateId: luminary.template.id,
                totalScenesInSceneGroup: sceneGroup.scenes.length,
                sceneGroupType: 'talking-head',
                creationDisplayGroupName: 'Multiple Speakers',
                mediaType: 'none',
                recommendedMediaSource: null,
                isVirtualDesign: false,
                isMultipleSpeakersScene: true,
            };
            analytics.track('Overlay added', analyticsPayload);
        });

        call(checkpointVideoOperation, systemTriggered, parentActionGroup);
    },
);
